import { useQueryClient } from '@tanstack/react-query'
import { SendHorizonal, UserPlusIcon } from 'lucide-react'
import { useState } from 'react'
import { queryKeys } from 'src/api/db/queryKeys'
import { useGenerateInvitationLink } from 'src/api/server/useGenerateInvitationLink'
import { Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'src/shared/components/ui/dialog'
import { Textarea } from 'src/shared/components/ui/textarea'
import { useLocalization } from 'src/shared/hooks/useLocalization'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const parseEmails = (value: string) =>
  value
    .split(',')
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean)

export const TableActions = ({
  selectedRowCount,
  totalRowCount,
  organizationId,
}: {
  selectedRowCount: number
  totalRowCount: number
  organizationId: string
}) => {
  const { t } = useLocalization()
  const queryClient = useQueryClient()
  const generateInvitationLink = useGenerateInvitationLink()
  const [isInviteAdminModalOpen, setIsInviteAdminModalOpen] = useState(false)
  const [adminEmails, setAdminEmails] = useState('')

  const parsedEmails = parseEmails(adminEmails)
  const emailsAreValid =
    parsedEmails.length > 0 &&
    parsedEmails.every((email) => EMAIL_RE.test(email))
  const canSend = emailsAreValid && !generateInvitationLink.isPending

  const handleInviteDialogOpenChange = (open: boolean) => {
    setIsInviteAdminModalOpen(open)
    if (!open) setAdminEmails('')
  }

  const handleInvite = async () => {
    if (!canSend) return

    await generateInvitationLink
      .mutateAsync({
        emails: parsedEmails.join(','),
        organization_id: organizationId,
      })
      .then(() => {
        setAdminEmails('')
        setIsInviteAdminModalOpen(false)
        queryClient.invalidateQueries({ queryKey: [queryKeys.org_users] })
      })
  }

  return (
    <>
      <Row className="items-center justify-end gap-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {selectedRowCount} of {totalRowCount} row(s) selected.
        </div>

        <Button
          variant="secondary"
          onClick={() => setIsInviteAdminModalOpen(true)}
        >
          <UserPlusIcon className="h-4 w-4 mr-2" />
          {t('Invite people', 'Bjud in personer')}
        </Button>
      </Row>

      <Dialog
        open={isInviteAdminModalOpen}
        onOpenChange={handleInviteDialogOpenChange}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {t('Invite people', 'Bjud in personer')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'Invite people by entering one or more email addresses. Separate by commas.',
                'Bjud in personer genom att ange en eller flera e-postadresser. Separera med kommatecken.'
              )}
            </DialogDescription>
          </DialogHeader>

          <Textarea
            className="mt-4"
            placeholder="name@company.com, teammate@company.com"
            rows={5}
            value={adminEmails}
            onChange={(e) => setAdminEmails(e.target.value)}
            description={
              adminEmails.trim() && !emailsAreValid
                ? t(
                    'Enter valid email addresses, separated by commas.',
                    'Ange giltiga e-postadresser, separerade med kommatecken.'
                  )
                : undefined
            }
          />

          <Row className="justify-between">
            <Button
              className="ml-auto mt-4"
              onClick={handleInvite}
              disabled={!canSend}
              isLoading={generateInvitationLink.isPending}
            >
              {t('Send invites', 'Skicka inbjudningar')}
              <SendHorizonal className="ml-2 h-4 w-4" />
            </Button>
          </Row>
        </DialogContent>
      </Dialog>
    </>
  )
}
